import { $ } from "../shared/globals.js";

export function createNavbarFeature({ context }) {
    const { $body, $navbar, $menuButton, $mainBrandBox } = context;
    const NAVBAR_SCROLLED_THRESHOLD = 10;
    const NAVBAR_HIDE_DELTA = 8;
    const MOBILE_NAV_MQ = window.matchMedia("(max-width: 767px)");

    let lastNavbarScrollTop = 0;
    let initialized = false;

    function getScrollTop() {
        return Math.max(window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0, 0);
    }

    function isMenuOpen() {
        return $menuButton.hasClass("w--current") || $menuButton.hasClass("w--open");
    }

    function normalizeTheme(themeValue) {
        return typeof themeValue === "string" && themeValue.trim() ? themeValue.trim().toLowerCase() : "default";
    }

    function getFallbackTheme() {
        return normalizeTheme($body.attr("nav-theme"));
    }

    function resolveThemeFromViewport() {
        if (!$navbar.length) return getFallbackTheme();
        if (isMenuOpen()) {
            return normalizeTheme($navbar.attr("data-nav-theme") || $mainBrandBox.attr("data-nav-theme") || $body.attr("nav-theme"));
        }

        const navbarRect = $navbar.get(0).getBoundingClientRect();
        const probeX = Math.min(Math.max(Math.round(navbarRect.left + navbarRect.width / 2), 0), window.innerWidth - 1);
        const probeY = Math.min(Math.max(Math.ceil(navbarRect.bottom) + 1, 0), window.innerHeight - 1);
        const probedElement = document.elementFromPoint(probeX, probeY);
        const themedParent = probedElement?.closest?.("[nav-theme]");

        return normalizeTheme(themedParent?.getAttribute("nav-theme") || $body.attr("nav-theme"));
    }

    /**
     * Poniżej 767px: brak nawigacji po sekcjach (nav-theme). Po scrollu zawsze jasne (default / beige);
     * na górze strony — zgodnie z atrybutem body.
     * Desktop: jak wcześniej (elementFromPoint).
     */
    function resolveTheme() {
        if (MOBILE_NAV_MQ.matches) {
            if (isMenuOpen()) {
                return normalizeTheme(
                    $navbar.attr("data-nav-theme") || $mainBrandBox.attr("data-nav-theme") || $body.attr("nav-theme"),
                );
            }
            if (getScrollTop() <= NAVBAR_SCROLLED_THRESHOLD) {
                return getFallbackTheme();
            }
            return "default";
        }
        return resolveThemeFromViewport();
    }

    function applyTheme(theme) {
        const normalizedTheme = normalizeTheme(theme);

        $navbar.attr("data-nav-theme", normalizedTheme);

        if ($mainBrandBox.length) {
            $mainBrandBox.attr("data-nav-theme", normalizedTheme);
        }
    }

    function updateNavbarState() {
        if (!$navbar.length) return;

        const scrollTop = getScrollTop();
        const scrollDelta = scrollTop - lastNavbarScrollTop;

        $navbar.toggleClass("is-nav-scrolled", scrollTop > NAVBAR_SCROLLED_THRESHOLD);
        $mainBrandBox.toggleClass("is-brand-scrolled", scrollTop > NAVBAR_SCROLLED_THRESHOLD);

        if (scrollTop <= NAVBAR_SCROLLED_THRESHOLD || isMenuOpen()) {
            $navbar.removeClass("is-nav-hidden");
        } else if (scrollDelta > NAVBAR_HIDE_DELTA) {
            $navbar.addClass("is-nav-hidden");
        } else if (scrollDelta < -NAVBAR_HIDE_DELTA) {
            $navbar.removeClass("is-nav-hidden");
        }

        applyTheme(resolveTheme());
        lastNavbarScrollTop = scrollTop;
    }

    function init() {
        if (initialized) return;

        $(window).on("scroll.navbar-state resize.navbar-state", updateNavbarState);
        $menuButton.on("click.navbar-state", () => {
            requestAnimationFrame(updateNavbarState);
        });

        lastNavbarScrollTop = getScrollTop();
        updateNavbarState();
        initialized = true;
    }

    function destroy() {
        $(window).off("scroll.navbar-state resize.navbar-state");
        $menuButton.off("click.navbar-state");
        $navbar.removeAttr("data-nav-theme");
        $mainBrandBox.removeClass("is-brand-scrolled");
        $mainBrandBox.removeAttr("data-nav-theme");
        initialized = false;
    }

    return {
        init,
        destroy,
    };
}
